import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import {
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Event as MeetingIcon,
  Note as NoteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as OutgoingIcon,
  ArrowDownward as IncomingIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { addInteraction, deleteInteraction, Interaction } from '../redux/slices/leadSlice';

interface LeadInteractionsProps {
  leadId: string;
  interactions?: Interaction[];
}

const LeadInteractions: React.FC<LeadInteractionsProps> = ({ leadId, interactions = [] }) => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [newInteraction, setNewInteraction] = useState<Omit<Interaction, 'id' | 'date'>>({
    type: 'note',
    direction: 'outgoing',
    content: '',
    subject: '',
  });

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setNewInteraction({
      type: 'note',
      direction: 'outgoing',
      content: '',
      subject: '',
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setNewInteraction({
      ...newInteraction,
      type: event.target.value as 'email' | 'whatsapp' | 'call' | 'meeting' | 'note',
    });
  };

  const handleDirectionChange = (event: SelectChangeEvent) => {
    setNewInteraction({
      ...newInteraction,
      direction: event.target.value as 'incoming' | 'outgoing',
    });
  };

  const handleContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewInteraction({
      ...newInteraction,
      content: event.target.value,
    });
  };

  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewInteraction({
      ...newInteraction,
      subject: event.target.value,
    });
  };

  const handleAddInteraction = () => {
    const interaction: Interaction = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      ...newInteraction,
    };

    dispatch(addInteraction({ leadId, interaction }));
    handleCloseDialog();
  };

  const handleDeleteInteraction = (interactionId: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta interação?')) {
      dispatch(deleteInteraction({ leadId, interactionId }));
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return dateString;
    }
  };

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <EmailIcon />;
      case 'whatsapp':
        return <WhatsAppIcon style={{ color: '#25D366' }} />;
      case 'call':
        return <PhoneIcon style={{ color: '#0078D4' }} />;
      case 'meeting':
        return <MeetingIcon style={{ color: '#7719AA' }} />;
      case 'note':
      default:
        return <NoteIcon style={{ color: '#FF9800' }} />;
    }
  };

  const getInteractionTypeLabel = (type: string) => {
    switch (type) {
      case 'email':
        return 'E-mail';
      case 'whatsapp':
        return 'WhatsApp';
      case 'call':
        return 'Ligação';
      case 'meeting':
        return 'Reunião';
      case 'note':
      default:
        return 'Anotação';
    }
  };

  const sortedInteractions = [...interactions].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Histórico de Interações</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Nova Interação
        </Button>
      </Box>

      {sortedInteractions.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ my: 4, textAlign: 'center' }}>
          Nenhuma interação registrada com este lead.
        </Typography>
      ) : (
        <List sx={{ width: '100%' }}>
          {sortedInteractions.map((interaction, index) => (
            <React.Fragment key={interaction.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                alignItems="flex-start"
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    aria-label="delete"
                    onClick={() => handleDeleteInteraction(interaction.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  {getInteractionIcon(interaction.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">
                        {interaction.subject || getInteractionTypeLabel(interaction.type)}
                      </Typography>
                      <Chip 
                        icon={interaction.direction === 'incoming' ? <IncomingIcon /> : <OutgoingIcon />}
                        label={interaction.direction === 'incoming' ? 'Recebido' : 'Enviado'}
                        size="small"
                        color={interaction.direction === 'incoming' ? 'info' : 'success'}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        {formatDate(interaction.date)}
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {interaction.content}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Dialog para adicionar nova interação */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Adicionar Nova Interação</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="interaction-type-label">Tipo de Interação</InputLabel>
                <Select
                  labelId="interaction-type-label"
                  value={newInteraction.type}
                  onChange={handleTypeChange}
                  label="Tipo de Interação"
                >
                  <MenuItem value="email">E-mail</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="call">Ligação</MenuItem>
                  <MenuItem value="meeting">Reunião</MenuItem>
                  <MenuItem value="note">Anotação</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="interaction-direction-label">Direção</InputLabel>
                <Select
                  labelId="interaction-direction-label"
                  value={newInteraction.direction}
                  onChange={handleDirectionChange}
                  label="Direção"
                >
                  <MenuItem value="incoming">Recebido</MenuItem>
                  <MenuItem value="outgoing">Enviado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(newInteraction.type === 'email') && (
              <Grid item xs={12}>
                <TextField
                  label="Assunto"
                  fullWidth
                  value={newInteraction.subject || ''}
                  onChange={handleSubjectChange}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Conteúdo"
                fullWidth
                multiline
                rows={4}
                value={newInteraction.content}
                onChange={handleContentChange}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleAddInteraction} 
            variant="contained" 
            color="primary"
            disabled={!newInteraction.content}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LeadInteractions; 